/* ============================================================
   Lab Effects — MERGED (tri-source) PRODUCT CARD
   assets/le-merged-card.js

   Drives <le-merged-card>. One custom element, two modes:

     solo  — the page is about one plant source (a CDT/CBS/BDT
             collection, or a related row). No pills. The card is
             painted once and never changes.
     multi — Shop All / search / aroma / homepage. Three code
             pills; hover PREVIEWS a source, leaving the pointer
             restores the committed one, click COMMITS it.

   The card link always points at that source's 2ml variant, so the
   PDP opens already on the source the shopper chose. The hex + is
   NOT touched here — it opens the shared quick-add slide-up and is
   handled by le-quick-add.js.
   ============================================================ */
(function () {
  var money = function (cents) {
    if (window.Shopify && Shopify.formatMoney) return Shopify.formatMoney(cents);
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  };

  function build(el) {
    var sources;
    try { sources = JSON.parse(el.dataset.sources || '[]'); }
    catch (e) { console.error('[le-merged-card] bad source payload', e); return; }
    if (!sources.length) return;

    var multi = el.dataset.mode === 'multi';
    var committed = parseInt(el.dataset.start, 10) || 0;
    if (committed >= sources.length) committed = 0;

    var refs = {
      link: el.querySelector('[data-mcard-link]'),
      bottle: el.querySelector('[data-mcard-bottle]'),
      cat: el.querySelector('[data-mcard-cat]'),
      icon: el.querySelector('[data-mcard-icon]'),
      price: el.querySelector('[data-mcard-price]'),
      compare: el.querySelector('[data-mcard-compare]'),
      codes: el.querySelector('[data-mcard-codes]')
    };

    function paint(i) {
      var s = sources[i];
      if (!s) return;
      el.style.setProperty('--src', s.color);
      if (refs.link) refs.link.setAttribute('href', s.url);
      if (refs.bottle && s.bottle) refs.bottle.src = s.bottle;
      if (refs.cat) refs.cat.textContent = s.cat;
      if (refs.icon && s.icon) { refs.icon.src = s.icon; refs.icon.alt = s.cat; }
      if (refs.price) refs.price.textContent = money(s.price);
      if (refs.compare) {
        if (s.compareAt && s.compareAt > s.price) {
          refs.compare.textContent = money(s.compareAt);
          refs.compare.hidden = false;
        } else {
          refs.compare.hidden = true;
        }
      }
      if (refs.codes) {
        [].forEach.call(refs.codes.children, function (b, bi) {
          b.classList.toggle('is-on', bi === i);
          b.setAttribute('aria-pressed', bi === i ? 'true' : 'false');
        });
      }
    }

    if (multi && refs.codes) {
      sources.forEach(function (s, i) {
        /* A real link, not a button: the pill goes to that source's product, so
           it should be middle-clickable and crawlable like any other link. The
           card's own stretched link sits underneath and is opted out of in CSS
           via z-index, the same model as the hex +. */
        var a = document.createElement('a');
        a.className = 'le-mcard__code';
        a.href = s.url;
        a.style.setProperty('--c', s.color);
        a.setAttribute('aria-pressed', 'false');
        a.innerHTML = '<span class="le-mcard__dot"></span>' + s.abbr;
        a.addEventListener('mouseenter', function () { paint(i); });
        a.addEventListener('focus', function () { paint(i); });
        a.addEventListener('click', function (ev) {
          /* Click commits the source rather than navigating — the shopper is
             choosing, not leaving. The card (or this pill on a second click)
             takes them to the PDP. */
          if (committed !== i) {
            ev.preventDefault();
            committed = i;
            paint(committed);
          }
        });
        refs.codes.appendChild(a);
      });
      /* Restore the committed source when the pointer OR focus leaves the pill
         group. Both matter, because paint() rewrites the card's stretched link:
         previewing a source and then clicking the card (or the twist art, which
         is now part of that link) must go to the source the shopper actually
         committed to, never the one they merely hovered or tabbed through.
         focusout fires on every pill-to-pill move too, so check the new target
         is outside the group before restoring. */
      refs.codes.addEventListener('mouseleave', function () { paint(committed); });
      refs.codes.addEventListener('focusout', function (ev) {
        if (!refs.codes.contains(ev.relatedTarget)) paint(committed);
      });
    }

    paint(committed);
  }

  /* ==========================================================
     ROW ALIGNMENT

     A merged card is taller than a normal one, and CSS-grid stretches every
     card in a row to the tallest. Because cards centre their contents, that
     extra height pushes the SHORTER cards' eyebrow and title down, so a row
     containing a merged card reads as ragged left-to-right.

     CSS can't express "the row this element is in", so we resolve rows here by
     grouping the grid's visible children on offsetTop, then tag only the rows
     that hold a merged card with .le-row-top (which top-aligns them). Rows
     without a merged card are never touched and keep centring.
     ========================================================== */
  var ROW_CLASS = 'le-row-top';
  var scheduled = false;

  /* The grid child that holds this merged card — usually the card element
     itself, but tolerate a wrapper (<li>, a link) sitting between them. */
  function gridItemFor(mc, grid) {
    var el = mc;
    while (el && el.parentElement !== grid) el = el.parentElement;
    return el;
  }

  function alignRows() {
    /* Group the merged cards by the grid they live in. Working from the merged
       cards outwards keeps this O(cards): we never have to ask a normal card
       whether it contains one. */
    var grids = [];
    var merged = [];
    document.querySelectorAll('le-merged-card').forEach(function (mc) {
      var g = mc.parentElement;
      while (g && g.parentElement && getComputedStyle(g).display.indexOf('grid') === -1) {
        g = g.parentElement;
        if (g === document.body) { g = null; break; }
      }
      if (!g) return;
      var i = grids.indexOf(g);
      if (i === -1) { grids.push(g); merged.push([mc]); } else { merged[i].push(mc); }
    });

    grids.forEach(function (grid, gi) {
      var kids = [].slice.call(grid.children);

      /* READ phase — no writes in here, or 600+ cards means 600+ forced
         reflows. offsetTop is safe to read with the class already applied:
         align-items on a card's OWN grid changes neither where the card sits
         nor how tall it is (height is max(image track, body track) either way). */
      var hot = {};
      merged[gi].forEach(function (mc) {
        var item = gridItemFor(mc, grid);
        if (item && item.offsetHeight) hot[item.offsetTop] = true;
      });

      var tops = kids.map(function (el) {
        return el.offsetHeight ? el.offsetTop : null;   /* null = .is-hidden */
      });

      /* WRITE phase — and only on a real change, so the MutationObserver below
         doesn't see records for work we just did and loop forever. */
      kids.forEach(function (el, i) {
        var should = tops[i] !== null && hot[tops[i]] === true;
        var on = el.classList.contains(ROW_CLASS);
        if (should && !on) el.classList.add(ROW_CLASS);
        else if (!should && on) el.classList.remove(ROW_CLASS);
      });
    });
  }

  function scheduleAlign() {
    if (scheduled) return;
    scheduled = true;
    var run = function () {
      if (!scheduled) return;      /* whichever timer wins, the other no-ops */
      scheduled = false;
      alignRows();
    };
    /* rAF alone is not enough: a hidden or backgrounded tab starves it, and the
       flag would then latch on forever and block every later call. The timeout
       is the floor that guarantees the pass actually happens. */
    requestAnimationFrame(run);
    setTimeout(run, 120);
  }

  function watchRows() {
    var seen = [];
    document.querySelectorAll('le-merged-card').forEach(function (mc) {
      var grid = mc.parentElement;
      while (grid && grid.parentElement && getComputedStyle(grid).display.indexOf('grid') === -1) {
        grid = grid.parentElement;
        if (grid === document.body) { grid = null; break; }
      }
      if (!grid || seen.indexOf(grid) !== -1) return;
      seen.push(grid);
      /* Column count changes and collection filtering both change row
         membership; the grid resizing covers the first, mutations the second. */
      if (window.ResizeObserver) new ResizeObserver(scheduleAlign).observe(grid);
      if (window.MutationObserver) {
        new MutationObserver(scheduleAlign).observe(grid, {
          childList: true, subtree: true, attributes: true, attributeFilter: ['class']
        });
      }
    });
    scheduleAlign();
  }

  window.addEventListener('resize', scheduleAlign);
  window.addEventListener('load', scheduleAlign);
  document.addEventListener('shopify:section:load', watchRows);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchRows);
  } else {
    watchRows();
  }

  if (window.customElements && !customElements.get('le-merged-card')) {
    customElements.define('le-merged-card', class extends HTMLElement {
      connectedCallback() {
        if (!this.__le) { this.__le = true; build(this); }
        scheduleAlign();
      }
    });
  } else {
    document.querySelectorAll('le-merged-card').forEach(build);
  }
})();
